/**
 * Fantasy Console 0 - Sprite Editor Tab
 *
 * Full-featured sprite editor with pixel-level editing, drawing tools,
 * and sprite manipulation capabilities.
 */

import { useCallback, useEffect, useMemo } from 'react'
import { useCart } from '../../context/CartContext'
import { usePersistedTabState } from '../../context/EditorTabStateContext'
import {
  ColorIndex,
  TOTAL_SPRITES,
  SPRITES_PER_ROW,
  decodeBase64,
  createEmptySpriteSheet,
} from '@fc0/types'
import { extractSprite, insertSprite } from '../../utils/spriteUtils'
import { useEditorHistory } from '../../hooks/useEditorHistory'
import {
  PixelEditor,
  ColorPalette,
  SpriteActions,
  DrawingTool,
} from '../SpriteEditor'
import { SpriteSheetPicker } from '../shared/SpriteSheetPicker'
import { ToolBar, type ToolDef } from '../shared/ToolBar'
import {
  PencilIcon,
  FillIcon,
  RectIcon,
  EraserIcon,
  PickerIcon,
} from '../shared/icons'
import styles from './SpriteEditorTab.module.css'
import editorStyles from '../../styles/editor.module.css'

function LineIcon() {
  return (
    <svg
      width='16'
      height='16'
      viewBox='0 0 16 16'
      fill='none'
      stroke='currentColor'
      strokeWidth='2'
      strokeLinecap='round'
    >
      <line x1='3' y1='13' x2='13' y2='3' />
    </svg>
  )
}

const spriteTools: ToolDef<DrawingTool>[] = [
  { id: 'pencil', label: 'Pencil', shortcut: 'P', icon: <PencilIcon /> },
  { id: 'fill', label: 'Fill', shortcut: 'F', icon: <FillIcon /> },
  { id: 'line', label: 'Line', shortcut: 'L', icon: <LineIcon /> },
  { id: 'rect', label: 'Rectangle', shortcut: 'R', icon: <RectIcon /> },
  { id: 'eraser', label: 'Eraser', shortcut: 'E', icon: <EraserIcon /> },
  { id: 'picker', label: 'Color Picker', shortcut: 'I', icon: <PickerIcon /> },
]

export function SpriteEditorTab() {
  const { cart, updateSprites, cartLoadVersion } = useCart()

  // Convert sprites data to Uint8Array for components
  const spritesArray = useMemo(
    () =>
      cart.sprites ? decodeBase64(cart.sprites) : createEmptySpriteSheet(),
    [cart.sprites],
  )

  // Undo/redo history (64 entries)
  const {
    pushState: handleSpritesChange,
    undo,
    redo,
    resetHistory,
  } = useEditorHistory(updateSprites)

  // Reset history when a new cart is loaded
  useEffect(() => {
    resetHistory(spritesArray)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cartLoadVersion, resetHistory])

  // Editor state (persisted across tab switches)
  const [selectedSprite, setSelectedSprite] = usePersistedTabState(
    'sprites:selectedSprite',
    0,
  )
  const [selectedColor, setSelectedColor] = usePersistedTabState<ColorIndex>(
    'sprites:selectedColor',
    7,
  )
  const [selectedTool, setSelectedTool] = usePersistedTabState<DrawingTool>(
    'sprites:selectedTool',
    'pencil',
  )
  const [clipboard, setClipboard] = usePersistedTabState<Uint8Array | null>(
    'sprites:clipboard',
    null,
  )

  // Handle color picking from pixel editor
  const handleColorPick = useCallback((color: ColorIndex) => {
    setSelectedColor(color)
    setSelectedTool('pencil')
  }, [])

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (
        e.target instanceof HTMLInputElement ||
        e.target instanceof HTMLTextAreaElement ||
        e.target instanceof HTMLSelectElement ||
        (e.target instanceof HTMLElement && e.target.isContentEditable)
      ) {
        return
      }

      // If a focused control already handled this key, don't double-handle it here.
      if (e.defaultPrevented) return

      const key = e.key.toLowerCase()

      // Tool shortcuts
      if (!e.ctrlKey && !e.metaKey && !e.altKey && !e.shiftKey) {
        switch (key) {
          case 'p':
            setSelectedTool('pencil')
            e.preventDefault()
            break
          case 'f':
            setSelectedTool('fill')
            e.preventDefault()
            break
          case 'e':
            setSelectedTool('eraser')
            e.preventDefault()
            break
          case 'i':
            setSelectedTool('picker')
            e.preventDefault()
            break
          case 'l':
            setSelectedTool('line')
            e.preventDefault()
            break
          case 'r':
            setSelectedTool('rect')
            e.preventDefault()
            break
          case '[':
            setSelectedSprite((prev) => Math.max(0, prev - 1))
            e.preventDefault()
            break
          case ']':
            setSelectedSprite((prev) => Math.min(TOTAL_SPRITES - 1, prev + 1))
            e.preventDefault()
            break
        }

        // Color shortcuts: 0-9 for colors 0-9
        if (/^[0-9]$/.test(key) && !e.shiftKey) {
          setSelectedColor(parseInt(key, 10) as ColorIndex)
          e.preventDefault()
        }
      }

      // Shift + 0-5 for colors 10-15
      if (e.shiftKey && !e.ctrlKey && !e.metaKey && !e.altKey) {
        const arr = [41, 33, 64, 35, 36, 37]
        const charCode = key.charCodeAt(0)
        if (arr.includes(charCode)) {
          setSelectedColor((arr.indexOf(charCode) + 10) as ColorIndex)
          e.preventDefault()
        }
      }

      // Undo/Redo and Copy/Paste shortcuts
      if (e.ctrlKey || e.metaKey) {
        if (key === 'z' && !e.shiftKey) {
          undo()
          e.preventDefault()
        } else if (key === 'z' && e.shiftKey) {
          redo()
          e.preventDefault()
        } else if (key === 'y') {
          redo()
          e.preventDefault()
        } else if (key === 'c') {
          setClipboard(extractSprite(spritesArray, selectedSprite))
          e.preventDefault()
        } else if (key === 'v') {
          if (clipboard) {
            handleSpritesChange(
              insertSprite(spritesArray, selectedSprite, clipboard),
            )
            e.preventDefault()
          }
        }
      }

      // Arrow keys for sprite navigation
      if (!e.ctrlKey && !e.shiftKey && !e.altKey && !e.metaKey) {
        const currentCol = selectedSprite % SPRITES_PER_ROW
        const currentRow = Math.floor(selectedSprite / SPRITES_PER_ROW)

        switch (e.key) {
          case 'ArrowLeft':
            if (currentCol > 0) {
              setSelectedSprite(selectedSprite - 1)
              e.preventDefault()
            }
            break
          case 'ArrowRight':
            if (currentCol < SPRITES_PER_ROW - 1) {
              setSelectedSprite(selectedSprite + 1)
              e.preventDefault()
            }
            break
          case 'ArrowUp':
            if (currentRow > 0) {
              setSelectedSprite(selectedSprite - SPRITES_PER_ROW)
              e.preventDefault()
            }
            break
          case 'ArrowDown':
            if (currentRow < SPRITES_PER_ROW - 1) {
              setSelectedSprite(selectedSprite + SPRITES_PER_ROW)
              e.preventDefault()
            }
            break
        }
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [selectedSprite, spritesArray, clipboard, handleSpritesChange, undo, redo])

  return (
    <div className={editorStyles.editorTabContainer}>
      {/* Main Editor Area */}
      <div className={editorStyles.editorArea}>
        {/* Left: Sprite Sheet */}
        <div className={styles.spriteSheetPane}>
          <SpriteSheetPicker
            sprites={spritesArray}
            selectedIndex={selectedSprite}
            onSelect={setSelectedSprite}
            scale={4}
            label='Sprite Sheet'
          />
          <SpriteActions
            sprites={spritesArray}
            selectedSprite={selectedSprite}
            onSpritesChange={handleSpritesChange}
            clipboard={clipboard}
            onClipboardChange={setClipboard}
            displaySheetActions
          />
        </div>

        {/* Right: Sprite Editor & Color Palette */}
        <div className={styles.pixelEditorPane}>
          <div className={styles.pixelEditorContainer}>
            <ColorPalette
              selectedColor={selectedColor}
              onSelectColor={setSelectedColor}
            />
            <ToolBar
              label=''
              tools={spriteTools}
              activeTool={selectedTool}
              onSelectTool={setSelectedTool}
            />
          </div>

          <div className={styles.pixelEditorContainer}>
            <PixelEditor
              sprites={spritesArray}
              selectedSprite={selectedSprite}
              selectedColor={selectedColor}
              selectedTool={selectedTool}
              onSpritesChange={handleSpritesChange}
              onColorPick={handleColorPick}
              canvasSize={280}
            />
            <SpriteActions
              sprites={spritesArray}
              selectedSprite={selectedSprite}
              onSpritesChange={handleSpritesChange}
              clipboard={clipboard}
              onClipboardChange={setClipboard}
              displayEditorActions
            />
          </div>
        </div>
      </div>
    </div>
  )
}
